import React from 'react';

import type { FormattedString } from '../types';
import {
  buttonClassName,
  ButtonStyling,
  ButtonVariant,
  DEFAULT_VARIANT,
  DEFAULT_SIZE,
} from './styling';
import { CaretDownIcon } from '@phosphor-icons/react';
import { RCLoadingBars, RCTooltip } from '../';

/* eslint-disable react/require-default-props, react/jsx-props-no-spreading */

// --- Internal interfaces ---

interface SharedProps extends Pick<ButtonStyling, 'variant' | 'size' | 'fullWidth'>{
  'aria-disabled'?: boolean;
}

interface IconOnlyProps {
  /** The `link` variant is text-only by nature (boxless, always-underlined), so it's not a valid
   * icon-only appearance — use a boxed variant for icon-only buttons. */
  variant?: Exclude<ButtonVariant, 'link'>;
  /**
   * The icon to display. Providing this prop switches the button to icon-only mode.
   * Use `leadingIcon` / `trailingIcon` for standard icon+text buttons instead.
   */
  iconOnly: JSX.Element;
  /** Required accessible label. Used as `aria-label` and default tooltip text. */
  iconLabel: string;
  /** Optional tooltip text override. Defaults to `iconLabel`. */
  children?: FormattedString;
  /** Hides the tooltip. Only use when the icon's meaning is unambiguous in context. */
  hideTooltip?: boolean;
}

interface OptionalIconProps {
  iconOnly?: never;
  /** The button text. */
  children: FormattedString;
  /** Icon displayed before the button text. */
  leadingIcon?: JSX.Element;
  /** Icon displayed after the button text. Pass `'dropdown'` for the built-in caret. */
  trailingIcon?: 'dropdown' | JSX.Element;
  iconOnlyAt?: never;
}

interface ResponsiveIconOnlyProps {
  /** The `link` variant is text-only by nature, so it can't collapse to an icon-only appearance. */
  variant?: Exclude<ButtonVariant, 'link'>;
  /** Changes to icon-only at this responsive breakpoint. The (visually-hidden) text still names
   * the button via `aria-labelledby`, so no separate accessible label is needed. */
  iconOnlyAt: 'mobile' | 'tablet';
}

interface ResponsiveLeadingIconOnlyProps extends ResponsiveIconOnlyProps {
  iconOnly?: never;
  /** The button text. Hidden at the `iconOnlyAt` breakpoint. */
  children: FormattedString;
  /** Required — persists as the sole visible element when collapsed. */
  leadingIcon: JSX.Element;
  /** Optional. Cannot be `'dropdown'` — a caret-only collapsed button has no meaning. */
  trailingIcon?: JSX.Element;
}

interface ResponsiveTrailingIconOnlyProps extends ResponsiveIconOnlyProps {
  iconOnly?: never;
  /** The button text. Hidden at the `iconOnlyAt` breakpoint. */
  children: FormattedString;
  /** Optional icon before the button text. */
  leadingIcon?: JSX.Element;
  /** Required — persists as the sole visible element when collapsed. Cannot be `'dropdown'`. */
  trailingIcon: JSX.Element;
}

export interface ButtonElementProps extends SharedProps {
  /** Appends `type="submit"` to the button for use inside forms. */
  isSubmit?: boolean;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  disabled?: boolean;
  id?: string;
  title?: string;
  form?: string;
  /** Hides the button from assistive tech and also removes it from the tab order, since a focusable
   * element with no accessible presence strands keyboard users. Pointer clicks still work. */
  'aria-hidden'?: boolean;
  /**
   * Shows a loading indicator and ignores clicks (including form submission) while true. The
   * indicator takes the leading icon's place if there is one, or else the trailing icon's (not the
   * `'dropdown'` caret's), so the label stays visible. Otherwise it's overlaid on the button, which
   * keeps its width. The button stays focusable and keeps its accessible name.
   *
   * Pass it as `false` when idle, not `undefined`, so the indicator fades in and out.
   */
  loading?: boolean;
}

export type LinkElementFunction = (c: React.ReactNode) => React.ReactElement;
interface LinkElementProps extends SharedProps {
  /**
   * Use with `<CoreLink />` or another routing component.
   * For example: `renderLink={c => <CoreLink to="...">{c}</CoreLink>}`
   */
  renderLink: LinkElementFunction;
  /** Prevents navigation and applies visual/ARIA disabled state. Prefer over `aria-disabled` alone. */
  disabled?: boolean;
  /** Set on the link element. Overrides an `id` set inside `renderLink`. */
  id?: string;
}

// Cast target for standard branches — covers both collapsible and non-collapsible cases.
interface StandardPropsWide {
  iconOnly?: never;
  children: FormattedString;
  leadingIcon?: JSX.Element;
  trailingIcon?: 'dropdown' | JSX.Element;
  iconOnlyAt?: 'mobile' | 'tablet';
}

// --- Public API types ---

/**
 * Props for RCButton, which always renders a `<button>` element.
 * Use `RCButtonLink` for link-element variants, `RCButtonSpan` for span variants.
 *
 * Icon modes:
 *   - `iconOnly={<Icon />}` → icon-only; `iconLabel` required
 *   - `leadingIcon` / `trailingIcon` → standard icon+text button
 *   - `iconOnlyAt` → changes to icon-only at a responsive breakpoint;
 *     either `leadingIcon` or `trailingIcon` required (not `'dropdown'`)
 */
export type RCButtonProps =
  | (ButtonElementProps & IconOnlyProps)
  | (ButtonElementProps & OptionalIconProps)
  | (ButtonElementProps & ResponsiveLeadingIconOnlyProps)
  | (ButtonElementProps & ResponsiveTrailingIconOnlyProps);

/** Props for RCButtonLink, which renders via a link element function (e.g. CoreLink). */
export type RCButtonLinkProps =
  | (LinkElementProps & IconOnlyProps)
  | (LinkElementProps & OptionalIconProps)
  | (LinkElementProps & ResponsiveLeadingIconOnlyProps)
  | (LinkElementProps & ResponsiveTrailingIconOnlyProps);

/**
 * The button-element, standard-icon subset of RCButtonProps.
 * Use this for composition components that always render `<button>` elements
 * (e.g. RCModalBase.Actions), to avoid the distributive-Omit issue with union types.
 */
export interface RCButtonStandardButtonProps extends ButtonElementProps, OptionalIconProps {}

/**
 * Props for RCButtonSpan, which renders a `<span>` styled as a button.
 * Supports the same icon modes as RCButton except the responsive `iconOnlyAt`:
 *   - `iconOnly={<Icon />}` → icon-only; `iconLabel` required
 *   - `leadingIcon` / `trailingIcon` → standard icon+text
 */
export type RCButtonSpanProps =
  | (SharedProps & IconOnlyProps)
  | (SharedProps & OptionalIconProps);

// --- Render helpers ---

function resolveTrailingIcon(trailingIcon: 'dropdown' | JSX.Element | undefined) {
  return {
    trailingIconEl: trailingIcon !== 'dropdown' ? trailingIcon : undefined,
    iconDropdown: trailingIcon === 'dropdown',
  };
}

interface StandardContentProps {
  children: FormattedString;
  leadingIcon?: JSX.Element;
  trailingIconEl?: JSX.Element;
  iconDropdown: boolean;
  iconOnlyAt?: 'mobile' | 'tablet';
  /** id for the text span so an `iconOnlyAt` button can name itself via `aria-labelledby`. */
  textId?: string;
}

function StandardContent({ children, leadingIcon, trailingIconEl, iconDropdown, iconOnlyAt, textId }: StandardContentProps) {
  return (
    <>
      {leadingIcon && <RCButtonIcon icon={leadingIcon} />}
      {iconOnlyAt ? <span id={textId} className="rc-button__text">{children}</span> : children}
      {iconDropdown
        ? <span className="rc-button__caret" aria-hidden><CaretDownIcon weight="bold" /></span>
        : trailingIconEl && <RCButtonIcon icon={trailingIconEl} />
      }
    </>
  );
}

interface RCButtonIconProps {
  icon: JSX.Element;
}

// The button always carries its own accessible name (children, or `iconLabel`
// for icon-only / iconOnlyAt), so the icon is decorative — hide it from AT.
function RCButtonIcon({ icon }: RCButtonIconProps) {
  return (
    <span className="rc-button__icon" aria-hidden>
      {icon}
    </span>
  );
}

// Stacks content and the loading indicator in one cell, so they cross-fade in place and the
// content keeps its size (and the button its accessible name) while hidden.
function RCButtonLoadingStack({ children }: { children: React.ReactNode }) {
  return (
    <span className="rc-button__stack">
      <span className="rc-button__content">{children}</span>
      <span className="rc-button__loader" aria-hidden><RCLoadingBars size="small" /></span>
    </span>
  );
}

// Refuses activation while keeping the button focusable: `aria-disabled` rather than `disabled`
// (which drops focus to <body>), and a click handler that cancels the default action, so a submit
// button doesn't submit its form, and stops the click there, as a disabled button's click never
// reaches its ancestors.
function inertButtonProps(onClick: React.MouseEventHandler<HTMLButtonElement> | undefined, inert: boolean) {
  if (!inert) return { onClick };
  return {
    'aria-disabled': true,
    onClick: (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      e.stopPropagation();
    },
  };
}

// --- Components ---

/**
 * Renders a `<button>` element styled as an RCButton.
 * Use `RCButtonLink` for link variants, `RCButtonSpan` for span variants.
 */
export const RCButton = React.forwardRef<HTMLButtonElement, RCButtonProps>(function RCButton(props, ref) {
    // Names an `iconOnlyAt` button from its own (visually-hidden) text via `aria-labelledby`,
    // so the accessible name always matches the visible label and formatted children work.
    const textId = React.useId();

    if (props.iconOnly) {
      const {
        iconOnly, iconLabel,
        children, hideTooltip = false,
        variant = DEFAULT_VARIANT, size = DEFAULT_SIZE, fullWidth,
        isSubmit, loading, onClick, ...buttonProps
      } = props as ButtonElementProps & IconOnlyProps;
      const classes = buttonClassName({ variant, size, fullWidth, iconOnly: true, loading });
      const buttonEl = (
        <button
          className={classes}
          type={isSubmit ? 'submit' : 'button'}
          aria-label={iconLabel}
          aria-busy={loading || undefined}
          tabIndex={buttonProps['aria-hidden'] ? -1 : undefined}
          ref={ref}
          {...buttonProps}
          {...inertButtonProps(onClick, !!loading)}
        >
          <RCButtonIcon
            icon={loading === undefined ? iconOnly : <RCButtonLoadingStack>{iconOnly}</RCButtonLoadingStack>}
          />
        </button>
      );
      if (hideTooltip) return buttonEl;
      return (
        <RCTooltip text={children ?? iconLabel} style="buttonLabel" placement="bottom" visualOnly>
          {buttonEl}
        </RCTooltip>
      );
    }

    const {
      children, leadingIcon, trailingIcon,
      iconOnlyAt,
      variant = DEFAULT_VARIANT, size = DEFAULT_SIZE, fullWidth,
      isSubmit, loading, onClick, ...buttonProps
    } = props as ButtonElementProps & StandardPropsWide;
    const { trailingIconEl, iconDropdown } = resolveTrailingIcon(trailingIcon);
    const classes = buttonClassName({
      variant, size, fullWidth,
      hasLeadingIcon: !!leadingIcon,
      hasTrailingIcon: !!trailingIconEl || iconDropdown,
      iconOnlyAt,
      loading,
    });
    // Once `loading` is passed, the loading structure stays rendered so the indicator can fade in
    // and out. The indicator shares an icon's slot when there is one, leading first, so the label
    // stays visible; otherwise it shares the whole button with the content. The dropdown caret
    // isn't an icon slot.
    const loadable = loading !== undefined;
    const loaderSlot = !loadable ? undefined : (leadingIcon && 'leading') || (trailingIconEl && 'trailing') || 'button';
    const content = (
      <StandardContent
        leadingIcon={loaderSlot === 'leading'
          ? <RCButtonLoadingStack>{leadingIcon}</RCButtonLoadingStack>
          : leadingIcon}
        trailingIconEl={loaderSlot === 'trailing'
          ? <RCButtonLoadingStack>{trailingIconEl}</RCButtonLoadingStack>
          : trailingIconEl}
        iconDropdown={iconDropdown}
        iconOnlyAt={iconOnlyAt}
        textId={textId}
      >
        {children}
      </StandardContent>
    );
    return (
      <button
        className={classes}
        type={isSubmit ? 'submit' : 'button'}
        aria-labelledby={iconOnlyAt ? textId : undefined}
        aria-busy={loading || undefined}
        tabIndex={buttonProps['aria-hidden'] ? -1 : undefined}
        ref={ref}
        {...buttonProps}
        {...inertButtonProps(onClick, !!loading)}
      >
        {loaderSlot === 'button' ? <RCButtonLoadingStack>{content}</RCButtonLoadingStack> : content}
      </button>
    );
  });

RCButton.displayName = 'RCButton';

/**
 * Renders a `<span>` styled as a button. Use for non-interactive placeholders such as
 * loading states or visual CTAs inside an already-linked area.
 * Use `RCButton` for interactive buttons, `RCButtonLink` for navigable links.
 */
export const RCButtonSpan = React.forwardRef<HTMLSpanElement, RCButtonSpanProps>(
  function RCButtonSpan(props, ref) {
    if (props.iconOnly) {
      const {
        iconOnly, iconLabel,
        children, hideTooltip = false,
        variant = DEFAULT_VARIANT, size = DEFAULT_SIZE, fullWidth,
        ...spanProps
      } = props as SharedProps & IconOnlyProps;
      const classes = buttonClassName({ variant, size, fullWidth, iconOnly: true });
      // A non-interactive span can't take a bare `aria-label` (invalid ARIA) the way the `<button>` can —
      // `role="img"` makes it a labelled graphic so `iconLabel` is exposed as its accessible name.
      const spanEl = (
        <span className={classes} role="img" aria-label={iconLabel} ref={ref} {...spanProps}>
          <RCButtonIcon icon={iconOnly} />
        </span>
      );
      if (hideTooltip) return spanEl;
      return (
        <RCTooltip text={children ?? iconLabel} style="buttonLabel" placement="bottom" visualOnly>
          {spanEl}
        </RCTooltip>
      );
    }

    const {
      children, leadingIcon, trailingIcon,
      variant = DEFAULT_VARIANT, size = DEFAULT_SIZE, fullWidth,
      ...spanProps
    } = props as SharedProps & OptionalIconProps;
    const { trailingIconEl, iconDropdown } = resolveTrailingIcon(trailingIcon);
    const classes = buttonClassName({
      variant, size, fullWidth,
      hasLeadingIcon: !!leadingIcon,
      hasTrailingIcon: !!trailingIconEl || iconDropdown,
    });
    return (
      <span className={classes} ref={ref} {...spanProps}>
        <StandardContent
          leadingIcon={leadingIcon}
          trailingIconEl={trailingIconEl}
          iconDropdown={iconDropdown}
        >
          {children}
        </StandardContent>
      </span>
    );
  },
);

RCButtonSpan.displayName = 'RCButtonSpan';

type LinkProps = React.HTMLAttributes<HTMLAnchorElement> & {
  disabled?: boolean;
};

function buildLinkElement(
  renderLink: LinkElementFunction,
  content: React.ReactNode,
  ref: React.ForwardedRef<HTMLAnchorElement>,
  { disabled = false, ...extraProps }: LinkProps,
): React.ReactElement {
  const output = renderLink(content);

  const disabledProps = disabled ? {
    'data-disabled': true,
    'aria-disabled': true,
    onClickCapture(e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) {
      e.preventDefault();
      e.stopPropagation();
    },
    tabIndex: -1,
  } : {};

  return React.cloneElement(output, { ...extraProps, ref, ...disabledProps });
}

/**
 * Renders a link styled as a button, via a routing component passed to `renderLink`.
 * Example: `renderLink={c => <CoreLink to="...">{c}</CoreLink>}`
 * Use `RCButton` for interactive buttons, `RCButtonSpan` for non-interactive spans.
 */
export const RCButtonLink = React.forwardRef<HTMLAnchorElement, RCButtonLinkProps>(
  function RCButtonLink(props, ref) {
    // See RCButton: an `iconOnlyAt` link names itself from its own text via `aria-labelledby`.
    const textId = React.useId();

    if (props.iconOnly) {
      const {
        renderLink, iconOnly, iconLabel,
        children, hideTooltip = false,
        variant = DEFAULT_VARIANT, size = DEFAULT_SIZE, fullWidth,
        disabled, ...linkProps
      } = props as LinkElementProps & IconOnlyProps;
      const classes = buttonClassName({ variant, size, fullWidth, iconOnly: true });
      const linkEl = buildLinkElement(renderLink, <RCButtonIcon icon={iconOnly} />, ref,
        {
          className: classes,
          'aria-label': iconLabel,
          disabled,
          ...linkProps
        });
      if (hideTooltip) return linkEl;
      return (
        <RCTooltip text={children ?? iconLabel} style="buttonLabel" placement="bottom" visualOnly>
          {linkEl}
        </RCTooltip>
      );
    }

    const {
      renderLink, children, leadingIcon, trailingIcon,
      iconOnlyAt,
      variant = DEFAULT_VARIANT, size = DEFAULT_SIZE, fullWidth,
      disabled, ...linkProps
    } = props as LinkElementProps & StandardPropsWide;
    const { trailingIconEl, iconDropdown } = resolveTrailingIcon(trailingIcon);
    const classes = buttonClassName({
      variant, size, fullWidth,
      hasLeadingIcon: !!leadingIcon,
      hasTrailingIcon: !!trailingIconEl || iconDropdown,
      iconOnlyAt,
    });
    const content = (
      <StandardContent
        leadingIcon={leadingIcon}
        trailingIconEl={trailingIconEl}
        iconDropdown={iconDropdown}
        iconOnlyAt={iconOnlyAt}
        textId={textId}
      >
        {children}
      </StandardContent>
    );
    return buildLinkElement(renderLink, content, ref,
      {
        className: classes,
        'aria-labelledby': iconOnlyAt ? textId : undefined,
        disabled,
        ...linkProps
      });
  },
);

RCButtonLink.displayName = 'RCButtonLink';
